import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface Cn61ReportFilters {
  ppo: string;
  year: number;
  direction: 'INBOUND' | 'OUTBOUND' | 'ALL';
  includeCrr: boolean;
}

export interface Cn61InputData {
  weight?: {
    priority: number | null;
    nonPriority: number | null;
  };
  items?: {
    priority: number | null;
    nonPriority: number | null;
  };
}

export interface Cn61Request {
  currency: string;
  inboundCn61Input: Cn61InputData | null;
  outboundCn61Input: Cn61InputData | null;
}

export interface Cn61Data {
  flow: string;
  dto: {
    ppo: string;
    year: number;
    direction: string;
  };
  request: Cn61Request;
}

export interface Cn61LocationResponse {
  location: string;
}

export type Cn61Endpoint = 'reim' | 'upu';

export function useCn61Reports(
  filters: Cn61ReportFilters,
  endpoint: Cn61Endpoint = 'reim',
  enabled: boolean = true
) {
  return useQuery<Cn61Data>({
    queryKey: ['cn61Reports', filters, endpoint],
    queryFn: async (): Promise<Cn61Data> => {
      const params = new URLSearchParams({
        flow: endpoint,
        ppo: filters.ppo,
        year: filters.year.toString(),
        direction: filters.direction,
        includeCrr: filters.includeCrr.toString(),
      });

      const response = await axios.get(
        `/cn61Crr/cn61${endpoint.charAt(0).toUpperCase() + endpoint.slice(1)}?${params}`
      );
      return response.data as Cn61Data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// Export payload interface
export interface Cn61ExportPayload {
  currency: string;
  inboundCn61Input: Cn61InputData | null;
  outboundCn61Input: Cn61InputData | null;
}

// Export params interface
export interface Cn61ExportParams {
  flow: string;
  designatedOperator: string;
  year: string;
  direction: string;
  formatType: string;
}

// Export function
export async function exportCn61Data(
  payload: Cn61ExportPayload,
  params: Cn61ExportParams
): Promise<Response> {
  const { flow, designatedOperator, year, direction, formatType } = params;
  
  try {
    const response = await axios.post(`/cn61/cn61`, payload, {
      params: {
        flow,
        designatedOperator,
        year,
        direction,
        formatType
      },
      responseType: formatType === 'html' ? 'json' : 'blob',
    });

    // Convert axios response to fetch-like Response for compatibility
    const headers = new Headers();
    if (formatType === 'html') {
      headers.set('content-type', 'application/json');
    } else {
      // For PDF/XLS, preserve the content-type and content-disposition from the server
      headers.set('content-type', response.headers['content-type'] || 'application/octet-stream');
      
      // Pass through Content-Disposition header if present
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (contentDisposition) {
        headers.set('Content-Disposition', contentDisposition);
      }
    }

    return new Response(
      formatType === 'html' ? JSON.stringify(response.data) : response.data,
      {
        status: response.status,
        statusText: response.statusText,
        headers,
      }
    );
  } catch (error: any) {
    // Re-throw as a proper Response object
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    
    // For network errors
    throw error;
  }
}

// CRR Submission interface
export interface CrrSubmissionPayload {
  inbound?: {
    priority: Array<{
      remunerationGroup: {
        id: number;
        name: string;
        operator: {
          id: number;
          name: string;
          longName: string;
          disabled: boolean;
        };
        displayName: string;
        predefined: boolean;
        homeCountry: boolean;
      };
      formatP: {
        kg: number;
        item: number;
      };
      formatG: {
        kg: number;
        item: number;
      };
      formatE: {
        kg: number;
        item: number;
      };
    }>;
    nonPriority: Array<{
      remunerationGroup: {
        id: number;
        name: string;
        operator: {
          id: number;
          name: string;
          longName: string;
          disabled: boolean;
        };
        displayName: string;
        predefined: boolean;
        homeCountry: boolean;
      };
      formatP: {
        kg: number;
        item: number;
      };
      formatG: {
        kg: number;
        item: number;
      };
      formatE: {
        kg: number;
        item: number;
      };
    }>;
  };
  outbound?: {
    priority: Array<{
      remunerationGroup: {
        id: number;
        name: string;
        operator: {
          id: number;
          name: string;
          longName: string;
          disabled: boolean;
        };
        displayName: string;
        predefined: boolean;
        homeCountry: boolean;
      };
      formatP: {
        kg: number;
        item: number;
      };
      formatG: {
        kg: number;
        item: number;
      };
      formatE: {
        kg: number;
        item: number;
      };
    }>;
    nonPriority: Array<{
      remunerationGroup: {
        id: number;
        name: string;
        operator: {
          id: number;
          name: string;
          longName: string;
          disabled: boolean;
        };
        displayName: string;
        predefined: boolean;
        homeCountry: boolean;
      };
      formatP: {
        kg: number;
        item: number;
      };
      formatG: {
        kg: number;
        item: number;
      };
      formatE: {
        kg: number;
        item: number;
      };
    }>;
  };
}

// Submit CRR data function
export async function submitCrrData(
  payload: CrrSubmissionPayload,
  ppo: string,
  year: string,
  direction: string
): Promise<Response> {
  try {
    const response = await axios.post(`/cn61Crr/crr`, payload, {
      params: {
        ppo,
        year,
        direction
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Convert axios response to fetch-like Response for compatibility
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers({
        'content-type': 'application/json',
      }),
    });
  } catch (error: any) {
    // Re-throw as a proper Response object
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    
    // For network errors
    throw error;
  }
}

// CN61 CRR HTML request interfaces
export interface Cn61CrrHtmlParams {
  ppo: string;
  year: number;
  direction: 'INBOUND' | 'OUTBOUND';
  formatType: string;
}

// Request CN61 CRR HTML report function
export async function requestCn61CrrHtml(
  params: Cn61CrrHtmlParams,
  payload: CrrSubmissionPayload
): Promise<Response> {
  const { ppo, year, direction, formatType } = params;
  
  try {
    const response = await axios.post(`/cn61/report/crr/${ppo}/${year}/${direction}`, payload, {
      params: {
        formatType
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Convert axios response to fetch-like Response for compatibility
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers({
        'content-type': 'application/json',
      }),
    });
  } catch (error: any) {
    // Re-throw as a proper Response object
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    
    // For network errors
    throw error;
  }
}

export interface Cn61ReimParams {
  designatedOperator: string;
  year: string;
  direction: string;
}

// Function to fetch CN61 REIM data using the path-based endpoint
export async function fetchCn61ReimData(
  params: Cn61ReimParams
): Promise<Response> {
  const { designatedOperator, year, direction } = params;

  try {
    const response = await axios.post(`/cn61/reim/${designatedOperator}/${year}/${direction}`, {
      responseType: 'json',
      headers: {
        'Accept': 'application/json',
      },
    });

    // Convert axios response to fetch-like Response for compatibility
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers({
        'content-type': 'application/json',
      }),
    });
  } catch (error: any) {
    // Re-throw as a proper Response object
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    
    // For network errors
    throw error;
  }
}

export interface CrrDataParams {
  ppo: string;
  year: string;
  direction: string;
}

export interface CrrHtmlReportParams {
  ppo: string;
  year: string;
  direction: string;
  formatType: string;
}

// Fetch CRR data from /cn61/crr/{ppo}/{year}/{direction}
export async function fetchCrrData(
  params: CrrDataParams
): Promise<Response> {
  const { ppo, year, direction } = params;
  try {
    const response = await axios.post(`/cn61/crr/${ppo}/${year}/${direction}`, {}, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });
    return new Response(JSON.stringify(response.data), {
      status: response.status,
      statusText: response.statusText,
      headers: new Headers({
        'content-type': 'application/json',
      }),
    });
  } catch (error: any) {
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    throw error;
  }
}

// Request CRR HTML report from /cn61/report/crr/{ppo}/{year}/{direction}
export async function requestCrrHtmlReport(
  params: CrrHtmlReportParams,
  payload: any
): Promise<Response> {
  const { ppo, year, direction, formatType } = params;
  try {
    const response = await axios.post(
      `/cn61/report/crr/${ppo}/${year}/${direction}`,
      payload,
      {
        params: { formatType },
        // For PDF/XLS, we need blob response type
        responseType: formatType === 'html' ? 'json' : 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Convert axios response to fetch-like Response for compatibility
    const headers = new Headers();
    if (formatType === 'html') {
      headers.set('content-type', 'application/json');
    } else {
      // For PDF/XLS, preserve the content-type and content-disposition from the server
      headers.set('content-type', response.headers['content-type'] || 'application/octet-stream');
      
      // Pass through Content-Disposition header if present
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (contentDisposition) {
        headers.set('Content-Disposition', contentDisposition);
      }
    }

    return new Response(
      formatType === 'html' ? JSON.stringify(response.data) : response.data,
      {
        status: response.status,
        statusText: response.statusText,
        headers,
      }
    );
  } catch (error: any) {
    if (error.response) {
      return new Response(JSON.stringify(error.response.data || {}), {
        status: error.response.status,
        statusText: error.response.statusText || 'Request failed',
        headers: new Headers({
          'content-type': 'application/json',
        }),
      });
    }
    throw error;
  }
}